<template>
  <div class="rich-text-editor">
    <input
      ref="fileInput"
      type="file"
      accept="image/*"
      style="display: none"
      @change="handleImageSelect"
    />
    <input
      ref="genericFileInput"
      type="file"
      style="display: none"
      @change="handleGenericFileSelect"
    />

    <div class="rich-text-editor-wrapper">
      <QuillEditor
        :ref="editorRef"
        :key="editorKey"
        :options="editorOptions"
        :style="{ height: height }"
        @update:content="handleContentUpdate"
        @text-change="handleContentUpdate"
        @ready="onEditorReady"
      />
    </div>

    <Teleport to="body">
      <div
        v-if="showLinkPopup"
        class="rte-popover link-popover"
        ref="linkPopover"
        role="dialog"
        aria-modal="true"
        aria-label="Insert link"
        @keyup.esc="closeLinkPopup"
      >
        <div class="rte-popover__header">
          <span class="rte-popover__title">Create link</span>
          <button
            type="button"
            class="rte-popover__close"
            @click="closeLinkPopup"
            aria-label="Close"
          >
            <Icon icon="mdi:close" />
          </button>
        </div>
        <div class="link-popover__body">
          <div class="rte-field">
            <span class="rte-field__label">Link text</span>
            <Input
              v-model="linkText"
              type="text"
              size="sm"
              placeholder="Link display text"
              @keyup.enter="insertLink"
              @keyup.esc="closeLinkPopup"
            />
          </div>
          <div class="rte-field">
            <span class="rte-field__label">URL</span>
            <Input
              v-model="linkUrl"
              type="text"
              size="sm"
              placeholder="https://example.com"
              @keyup.enter="insertLink"
              @keyup.esc="closeLinkPopup"
            />
          </div>
          <div class="link-popover__actions">
            <Button variant="secondary" size="sm" @click="closeLinkPopup"> Cancel </Button>
            <Button
              variant="primary"
              size="sm"
              :disabled="!linkUrl || linkUrl.trim() === ''"
              @click="insertLink"
            >
              Apply
            </Button>
          </div>
        </div>
        <div ref="linkArrow" class="rte-popover__arrow"></div>
      </div>

      <div
        v-if="showImageResizePopup"
        class="rte-popover image-resize-popover"
        ref="imageResizePopover"
        role="dialog"
        aria-modal="true"
        aria-label="Resize image"
        @keyup.esc="closeImageResizePopup"
      >
        <div class="image-resize-popover__controls">
          <input
            ref="imageResizeInput"
            v-model.number="imageWidth"
            type="number"
            class="image-resize-popover__input"
            placeholder="Width"
            @keyup.enter="applyImageResize"
            @keyup.esc="closeImageResizePopup"
          />
          <span class="image-resize-popover__px">px</span>
          <input
            v-model.number="imageHeight"
            type="number"
            class="image-resize-popover__input"
            placeholder="Height"
            @keyup.enter="applyImageResize"
            @keyup.esc="closeImageResizePopup"
          />
          <span class="image-resize-popover__px">px</span>
          <button
            type="button"
            class="image-resize-popover__link"
            :class="{ 'image-resize-popover__link--active': maintainAspectRatio }"
            @click="toggleAspectRatio"
            title="Maintain aspect ratio"
          >
            <Icon icon="tabler:aspect-ratio" />
          </button>
          <button
            type="button"
            class="image-resize-popover__apply"
            @click="applyImageResize"
            title="Apply dimensions"
          >
            <Icon icon="mdi:check" />
          </button>
          <div class="image-resize-popover__divider"></div>
          <button
            type="button"
            class="image-resize-popover__link-img-btn"
            :class="{
              'image-resize-popover__link-img-btn--active': showImageLinkInput || !!imageLinkUrl,
            }"
            @click="toggleImageLinkInput"
            title="Link image"
          >
            <Icon icon="mdi:link-variant" />
          </button>
        </div>

        <div v-if="showImageLinkInput" class="image-resize-popover__url-row">
          <input
            ref="imageLinkInput"
            v-model="imageLinkUrl"
            type="text"
            class="image-resize-popover__url-input"
            placeholder="https://example.com"
            @keyup.enter="applyImageLink"
            @keyup.esc="showImageLinkInput = false"
          />
          <button
            type="button"
            class="image-resize-popover__url-apply"
            :disabled="!imageLinkUrl.trim()"
            @click="applyImageLink"
            title="Apply link"
          >
            <Icon icon="mdi:check" />
          </button>
          <button
            v-if="imageLinkUrl"
            type="button"
            class="image-resize-popover__url-remove"
            @click="removeImageLink"
            title="Remove link"
          >
            <Icon icon="mdi:link-variant-off" />
          </button>
        </div>
      </div>

      <div
        v-if="showImagePopup"
        class="rte-popover image-popover"
        ref="imagePopover"
        role="menu"
        aria-label="Insert image"
        @keyup.esc="closeImagePopup"
      >
        <Button
          variant="secondary"
          size="sm"
          icon="mdi:cloud-upload-outline"
          block
          :loading="isUploadingImage"
          :disabled="isUploadingImage"
          @click="triggerFileInput"
        >
          {{ isUploadingImage ? "Uploading…" : "Upload" }}
        </Button>
        <div ref="imageArrow" class="rte-popover__arrow"></div>
      </div>

      <div
        v-if="showFilePopup"
        class="rte-popover file-popover"
        ref="filePopover"
        role="menu"
        aria-label="Attach file"
        @keyup.esc="closeFilePopup"
      >
        <Button
          variant="secondary"
          size="sm"
          icon="mdi:cloud-upload-outline"
          block
          :loading="isUploadingFile"
          :disabled="isUploadingFile"
          @click="triggerGenericFileInput"
        >
          {{ isUploadingFile ? "Uploading…" : "Upload" }}
        </Button>
        <div ref="fileArrow" class="rte-popover__arrow"></div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import Button from "@/components/ui/Button.vue";
import Input from "@/components/ui/Input.vue";
import { Icon } from "@iconify/vue";
import { Quill, QuillEditor } from "@vueup/vue-quill";
import "@vueup/vue-quill/dist/vue-quill.snow.css";
import { computePosition, autoUpdate, offset, flip, shift, size, arrow } from "@floating-ui/dom";

const BlockEmbed = Quill.import("blots/block/embed");
class Html5VideoBlot extends BlockEmbed {
  static blotName = "html5-video";
  static tagName = "video";
  static className = "rte-html5-video";

  static create(value) {
    const node = super.create();
    const src = typeof value === "string" ? value : value?.src || "";
    const fileName = (value && typeof value === "object" && value.fileName) || "";
    node.setAttribute("src", src);
    node.setAttribute("controls", "controls");
    node.setAttribute("preload", "metadata");
    node.setAttribute("playsinline", "true");
    if (fileName) node.setAttribute("data-name", fileName);
    return node;
  }
  static value(node) {
    return {
      src: node.getAttribute("src"),
      fileName: node.getAttribute("data-name") || "",
    };
  }
}

class Html5AudioBlot extends BlockEmbed {
  static blotName = "html5-audio";
  static tagName = "audio";
  static className = "rte-html5-audio";

  static create(value) {
    const node = super.create();
    const src = typeof value === "string" ? value : value?.src || "";
    const fileName = (value && typeof value === "object" && value.fileName) || "";
    node.setAttribute("src", src);
    node.setAttribute("controls", "controls");
    node.setAttribute("preload", "metadata");
    if (fileName) node.setAttribute("data-name", fileName);
    return node;
  }
  static value(node) {
    return {
      src: node.getAttribute("src"),
      fileName: node.getAttribute("data-name") || "",
    };
  }
}
class IframeVideoBlot extends BlockEmbed {
  static blotName = "video";
  static tagName = "iframe";
  static create(value) {
    const node = super.create();
    const src = typeof value === "string" ? value : value?.src || "";
    node.setAttribute("src", src);
    node.setAttribute("frameborder", "0");
    node.setAttribute("allowfullscreen", "true");
    node.setAttribute(
      "allow",
      "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
    );
    return node;
  }
  static value(node) {
    return node.getAttribute("src");
  }
}
const InlineEmbed = Quill.import("blots/embed");

function humanSize(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return "";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n >= 10 || i === 0 ? Math.round(n) : n.toFixed(1)} ${units[i]}`;
}

function mimeIconSvg(mime, name) {
  const ext = (name || "").split(".").pop()?.toLowerCase() || "";
  const isPdf = mime === "application/pdf" || ext === "pdf";
  const isImg = (mime && mime.startsWith("image/")) || /^(png|jpe?g|gif|webp|svg|bmp)$/i.test(ext);
  const isVid = (mime && mime.startsWith("video/")) || /^(mp4|webm|mov|avi|mkv)$/i.test(ext);
  const isAud = (mime && mime.startsWith("audio/")) || /^(mp3|wav|ogg|m4a|aac|flac)$/i.test(ext);
  const isZip = /zip|compressed/.test(mime || "") || /^(zip|7z|rar|tar|gz)$/i.test(ext);
  const isDoc = /(word|document)/.test(mime || "") || /^(docx?|rtf)$/i.test(ext);
  const isXls = /(sheet|excel)/.test(mime || "") || /^(xlsx?|csv)$/i.test(ext);
  const variant = isPdf
    ? "pdf"
    : isDoc
      ? "doc"
      : isXls
        ? "xls"
        : isZip
          ? "zip"
          : isAud
            ? "aud"
            : isImg
              ? "img"
              : isVid
                ? "vid"
                : "file";

  if (isAud) {
    // music note glyph instead of file frame
    return `<span class="rte-file-icon rte-file-icon--aud"><svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg></span>`;
  }
  const base = `<path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/>`;
  let inner = "";
  if (isPdf || isDoc) {
    inner = `<line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><line x1="10" y1="9" x2="9" y2="9"/>`;
  } else if (isXls) {
    inner = `<line x1="8" y1="13" x2="10" y2="13"/><line x1="14" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="10" y2="17"/><line x1="14" y1="17" x2="16" y2="17"/>`;
  } else if (isZip) {
    inner = `<line x1="10" y1="13" x2="10" y2="13.01"/><line x1="10" y1="15" x2="10" y2="15.01"/><line x1="10" y1="17" x2="10" y2="17.01"/>`;
  } else if (isImg) {
    inner = `<circle cx="10" cy="13" r="1.5"/><path d="m20 17-3-3-5 5"/>`;
  } else if (isVid) {
    inner = `<polygon points="10 12 16 15 10 18"/>`;
  }

  return `<span class="rte-file-icon rte-file-icon--${variant}"><svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${base}${inner}</svg></span>`;
}

function escapeHtml(s) {
  return String(s ?? "").replace(
    /[&<>"']/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]
  );
}

class UploadPlaceholderBlot extends BlockEmbed {
  static blotName = "rte-upload-placeholder";
  static tagName = "div";
  static className = "rte-upload-placeholder";

  static create(value) {
    const node = super.create();
    const v = value || {};
    const isImage = v.kind === "image" && v.previewUrl;
    node.setAttribute("data-upload-id", v.id || "");
    node.setAttribute("data-kind", v.kind || "file");
    node.setAttribute("contenteditable", "false");
    node.classList.add("rte-upload-placeholder--uploading");
    node.classList.add(isImage ? "rte-upload-placeholder--image" : "rte-upload-placeholder--file");

    const fileName = escapeHtml(v.fileName || "Uploading…");
    const size = escapeHtml(humanSize(v.fileSize));
    const cancelBtn = `<button type="button" class="rte-upload-placeholder__cancel" data-action="cancel" aria-label="Cancel upload" tabindex="-1"><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>`;

    if (isImage) {
      node.innerHTML = `
        <div class="rte-upload-placeholder__preview">
          <img src="${escapeHtml(v.previewUrl)}" alt="" />
          <div class="rte-upload-placeholder__shade"></div>
        </div>
        <div class="rte-upload-placeholder__overlay">
          <div class="rte-upload-placeholder__name">${fileName}</div>
          <div class="rte-upload-placeholder__meta">
            <span class="rte-upload-placeholder__size">${size}</span>
            <span class="rte-upload-placeholder__status">Uploading 0%</span>
          </div>
          <div class="rte-upload-placeholder__track"><div class="rte-upload-placeholder__bar" style="width:0%"></div></div>
        </div>
        ${cancelBtn}`;
    } else {
      node.innerHTML = `
        <div class="rte-upload-placeholder__icon">${mimeIconSvg(v.mimeType, v.fileName)}</div>
        <div class="rte-upload-placeholder__body">
          <div class="rte-upload-placeholder__name">${fileName}</div>
          <div class="rte-upload-placeholder__meta">
            <span class="rte-upload-placeholder__size">${size}</span>
            <span class="rte-upload-placeholder__status">Uploading 0%</span>
          </div>
          <div class="rte-upload-placeholder__track"><div class="rte-upload-placeholder__bar" style="width:0%"></div></div>
        </div>
        ${cancelBtn}`;
    }
    return node;
  }
  static value(node) {
    return {
      id: node.getAttribute("data-upload-id"),
      kind: node.getAttribute("data-kind"),
    };
  }
}

class FileChipBlot extends InlineEmbed {
  static blotName = "file-chip";
  static tagName = "a";
  static className = "rte-file-chip";

  static create(value) {
    const node = super.create(value);
    const v = value || {};
    node.setAttribute("href", v.url || "");
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
    node.setAttribute("download", v.fileName || "");
    node.setAttribute("data-mime", v.mimeType || "");
    node.setAttribute("data-size", String(v.fileSize || ""));
    node.setAttribute("data-name", v.fileName || "");
    node.setAttribute("contenteditable", "false");
    const chipName = escapeHtml(v.fileName || "File");
    const chipSize = escapeHtml(humanSize(v.fileSize));
    node.innerHTML = `${mimeIconSvg(v.mimeType, v.fileName)}<span class="rte-file-chip__body"><span class="rte-file-chip__name">${chipName}</span><span class="rte-file-chip__meta">${chipSize}</span></span>`;
    return node;
  }
  static value(node) {
    return {
      url: node.getAttribute("href") || "",
      fileName: node.getAttribute("data-name") || node.getAttribute("download") || "",
      mimeType: node.getAttribute("data-mime") || "",
      fileSize: Number(node.getAttribute("data-size")) || 0,
    };
  }
}

function updatePlaceholderProgress(root, id, pct, statusText) {
  if (!root || !id) return;
  const node = root.querySelector(`.rte-upload-placeholder[data-upload-id="${id}"]`);
  if (!node) return;
  const bar = node.querySelector(".rte-upload-placeholder__bar");
  const status = node.querySelector(".rte-upload-placeholder__status");
  const clamped = Math.max(0, Math.min(100, pct));
  if (bar) bar.style.width = `${clamped}%`;
  if (status) status.textContent = statusText || `Uploading ${clamped}%`;
}

function setPlaceholderState(root, id, state) {
  if (!root || !id) return;
  const node = root.querySelector(`.rte-upload-placeholder[data-upload-id="${id}"]`);
  if (!node) return;
  ["uploading", "finalizing", "error"].forEach((s) =>
    node.classList.remove(`rte-upload-placeholder--${s}`)
  );
  node.classList.add(`rte-upload-placeholder--${state}`);
}

function findPlaceholderIndex(quill, id) {
  if (!quill || !quill.root) return -1;
  const node = quill.root.querySelector(`.rte-upload-placeholder[data-upload-id="${id}"]`);
  if (!node) return -1;
  const blot =
    (quill.scroll && typeof quill.scroll.find === "function" ? quill.scroll.find(node) : null) ||
    (typeof Quill.find === "function" ? Quill.find(node) : null);
  if (!blot) return -1;
  return quill.getIndex(blot);
}

const FONT_WHITELIST = [
  "arial",
  "georgia",
  "helvetica",
  "verdana",
  "trebuchet",
  "times-new-roman",
  "courier-new",
  "impact",
];

try {
  Quill.register(Html5VideoBlot, true);
  Quill.register(Html5AudioBlot, true);
  Quill.register(IframeVideoBlot, true);
  Quill.register(UploadPlaceholderBlot, true);
  Quill.register(FileChipBlot, true);

  const Font = Quill.import("formats/font");
  Font.whitelist = FONT_WHITELIST;
  Quill.register(Font, true);
} catch (e) {}

function transformToEmbedUrl(rawUrl) {
  if (!rawUrl || typeof rawUrl !== "string") return null;
  const url = rawUrl.trim();
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./, "");

    if (host === "youtu.be") {
      const id = parsed.pathname.replace(/^\//, "").split("/")[0];
      if (id) return `https://www.youtube.com/embed/${id}`;
    }
    if (host === "youtube.com" || host === "m.youtube.com" || host === "youtube-nocookie.com") {
      if (parsed.pathname.startsWith("/embed/")) return url;
      const v = parsed.searchParams.get("v");
      if (v) return `https://www.youtube.com/embed/${v}`;
    }
    if (host === "vimeo.com") {
      const id = parsed.pathname.replace(/^\//, "").split("/")[0];
      if (id && /^\d+$/.test(id)) return `https://player.vimeo.com/video/${id}`;
    }
    if (host === "player.vimeo.com") return url;
    if (host === "loom.com") {
      const parts = parsed.pathname.split("/").filter(Boolean);
      if (parts[0] === "share" && parts[1]) return `https://www.loom.com/embed/${parts[1]}`;
      if (parts[0] === "embed") return url;
    }
  } catch (e) {
    return null;
  }
  return null;
}

function isSafeUrl(url) {
  if (!url) return false;
  const trimmed = String(url).trim();
  if (trimmed.length === 0) return false;
  if (/^(javascript|data|vbscript|file):/i.test(trimmed)) return false;
  return /^(https?:\/\/|\/|mailto:|tel:)/i.test(trimmed);
}

export default {
  name: "RichTextEditor",
  components: {
    QuillEditor,
    Icon,
    Button,
    Input,
  },
  props: {
    modelValue: {
      type: String,
      default: "",
    },
    height: {
      type: String,
      default: "150px",
    },
    editorRef: {
      type: String,
      default: "quillEditor",
    },
    toolbar: {
      type: Array,
      default: null,
    },
    placeholder: {
      type: String,
      default: "",
    },
    hideToolbarItems: {
      type: Array,
      default: () => [],
    },
    enableImageUrl: {
      type: Boolean,
      default: true,
    },
    enableImageUpload: {
      type: Boolean,
      default: true,
    },
    enableFileAttach: {
      type: Boolean,
      default: true,
    },
    acceptsUploads: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue", "upload", "uploads-pending", "enter-pressed"],
  data() {
    return {
      showLinkPopup: false,
      linkUrl: "",
      linkText: "",
      showImagePopup: false,
      imageUrl: "",
      imageAlt: "",
      imageInsertMode: "url",
      isEditorReady: false,
      quillInstance: null,
      isUploadingImage: false,
      selectedFileName: "",
      showImageResizePopup: false,
      selectedImageSrc: null,
      selectedImageEl: null,
      imageWidth: null,
      imageHeight: null,
      originalImageWidth: null,
      originalImageHeight: null,
      maintainAspectRatio: true,
      showImageLinkInput: false,
      imageLinkUrl: "",
      imageClickHandler: null,
      pasteHandler: null,
      dropHandler: null,
      dragOverHandler: null,
      documentClickHandler: null,
      editorKey: 0,
      contentUpdateTimeout: null,
      showFilePopup: false,
      isUploadingFile: false,
      fileAttachName: "",
      pendingUploadCount: 0,
      floatingCleanups: {
        link: null,
        image: null,
        file: null,
        imageResize: null,
      },
      lastFocusedEl: null,
      escKeyHandler: null,
      defaultToolbarConfig: [
        [
          {
            font: ["", ...FONT_WHITELIST],
          },
          { header: [1, 2, 3, 4, 5, 6, false] },
        ],
        ["bold", "italic", "underline", "strike"],
        [{ color: [] }, { background: [] }],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ indent: "-1" }, { indent: "+1" }],
        [{ direction: "rtl" }],
        [{ align: [] }],
        ["link", "image", "attach-file", "formula"],
        ["clean"],
      ],
    };
  },
  computed: {
    editorOptions() {
      let toolbarConfig = this.toolbar || this.defaultToolbarConfig;

      if (this.hideToolbarItems && this.hideToolbarItems.length > 0) {
        toolbarConfig = this.filterToolbarItems(toolbarConfig, this.hideToolbarItems);
      }

      const toolbar = Array.isArray(toolbarConfig)
        ? {
            container: toolbarConfig,
            handlers: {
              image: this.imageHandler,
              link: this.linkHandler,
              "attach-file": this.fileAttachHandler,
            },
          }
        : typeof toolbarConfig === "object" && toolbarConfig.handlers
          ? {
              ...toolbarConfig,
              handlers: {
                ...toolbarConfig.handlers,
                image: this.imageHandler,
                link: this.linkHandler,
                "attach-file": this.fileAttachHandler,
              },
            }
          : {
              container: toolbarConfig,
              handlers: {
                image: this.imageHandler,
                link: this.linkHandler,
                "attach-file": this.fileAttachHandler,
              },
            };

      const options = {
        modules: {
          toolbar,
        },
        placeholder: this.placeholder || "",
      };

      return options;
    },
  },
  methods: {
    filterToolbarItems(config, itemsToHide) {
      if (!Array.isArray(config)) {
        return config;
      }

      return config
        .map((item) => {
          if (Array.isArray(item)) {
            const filtered = item.filter((subItem) => {
              if (typeof subItem === "string") {
                return !itemsToHide.includes(subItem);
              }
              if (typeof subItem === "object" && subItem !== null) {
                const keys = Object.keys(subItem);
                return !keys.some((key) => itemsToHide.includes(key));
              }
              return true;
            });
            return filtered.length > 0 ? filtered : null;
          }
          if (typeof item === "string") {
            return itemsToHide.includes(item) ? null : item;
          }
          if (typeof item === "object" && item !== null) {
            const keys = Object.keys(item);
            const hasHiddenKey = keys.some((key) => itemsToHide.includes(key));
            return hasHiddenKey ? null : item;
          }
          return item;
        })
        .filter((item) => item !== null);
    },
    linkHandler(value) {
      if (!this.isEditorReady) return;

      const editor = this.$refs[this.editorRef];
      if (!editor) return;

      try {
        const quill = editor.getQuill();
        if (!quill || !quill.root) return;

        const range = quill.getSelection(true);

        if (range && range.length > 0) {
          const selectedText = quill.getText(range.index, range.length);
          if (selectedText.startsWith("http")) {
            this.linkUrl = selectedText;
            this.linkText = "";
          } else {
            this.linkText = selectedText;
            this.linkUrl = "";
          }
        } else {
          const [link, offset] = quill.getLeaf(range.index);
          if (link && link.domNode && link.domNode.tagName === "A") {
            this.linkUrl = link.domNode.href || "";
            this.linkText = link.domNode.textContent || "";
          } else {
            this.linkUrl = "";
            this.linkText = "";
          }
        }

        this.rememberFocus();
        this.closeAllPopovers("link");
        this.showLinkPopup = true;
        this.$nextTick(() => {
          this.positionLinkPopover();
          const firstInput = this.$refs.linkPopover?.querySelector?.("input");
          firstInput?.focus();
        });
      } catch (error) {}
    },
    getToolbarButton(selector) {
      const toolbar = this.$el?.querySelector(".ql-toolbar");
      if (!toolbar) return null;
      return toolbar.querySelector(selector) || toolbar;
    },
    attachFloating(key, referenceEl, floatingEl, placement = "top-start", arrowEl = null) {
      this.teardownFloating(key);
      if (!referenceEl || !floatingEl) return;
      const middleware = [
        offset(10),
        flip({ padding: 8 }),
        shift({ padding: 8 }),
        size({
          padding: 8,
          apply({ availableWidth, elements }) {
            Object.assign(elements.floating.style, {
              maxWidth: `${Math.max(240, availableWidth)}px`,
            });
          },
        }),
      ];
      if (arrowEl) middleware.push(arrow({ element: arrowEl, padding: 6 }));

      const update = () => {
        computePosition(referenceEl, floatingEl, {
          placement,
          strategy: "fixed",
          middleware,
        })
          .then(({ x, y, placement: resolvedPlacement, middlewareData }) => {
            Object.assign(floatingEl.style, {
              position: "fixed",
              left: `${x}px`,
              top: `${y}px`,
            });
            if (arrowEl && middlewareData.arrow) {
              const { x: ax, y: ay } = middlewareData.arrow;
              const side = resolvedPlacement.split("-")[0];
              const staticSide = {
                top: "bottom",
                right: "left",
                bottom: "top",
                left: "right",
              }[side];
              Object.assign(arrowEl.style, {
                left: ax != null ? `${ax}px` : "",
                top: ay != null ? `${ay}px` : "",
                right: "",
                bottom: "",
                [staticSide]: "-5px",
              });
              arrowEl.dataset.side = side;
            }
          })
          .catch(() => {});
      };
      const cleanup = autoUpdate(referenceEl, floatingEl, update, {
        ancestorScroll: true,
        ancestorResize: true,
        elementResize: true,
      });
      this.floatingCleanups[key] = cleanup;
    },
    teardownFloating(key) {
      const cleanup = this.floatingCleanups?.[key];
      if (typeof cleanup === "function") {
        try {
          cleanup();
        } catch (e) {}
        this.floatingCleanups[key] = null;
      }
    },
    rememberFocus() {
      this.lastFocusedEl = document.activeElement;
    },
    closeAllPopovers(except) {
      if (except !== "link" && this.showLinkPopup) {
        this.showLinkPopup = false;
        this.linkUrl = "";
        this.linkText = "";
        this.teardownFloating("link");
      }
      if (except !== "image" && this.showImagePopup) {
        this.showImagePopup = false;
        this.imageUrl = "";
        this.imageAlt = "";
        this.resetUploadState();
        if (this.$refs.fileInput) this.$refs.fileInput.value = "";
        this.teardownFloating("image");
      }
      if (except !== "file" && this.showFilePopup) {
        this.showFilePopup = false;
        this.isUploadingFile = false;
        this.fileAttachName = "";
        if (this.$refs.genericFileInput) this.$refs.genericFileInput.value = "";
        this.teardownFloating("file");
      }
      if (except !== "imageResize" && this.showImageResizePopup) {
        this.closeImageResizePopup();
      }
    },
    restoreFocus() {
      if (this.lastFocusedEl && typeof this.lastFocusedEl.focus === "function") {
        try {
          this.lastFocusedEl.focus();
        } catch (e) {}
      }
      this.lastFocusedEl = null;
    },
    positionLinkPopover() {
      const ref = this.getToolbarButton("button.ql-link") || this.$el?.querySelector(".ql-toolbar");
      const popover = this.$refs.linkPopover;
      this.attachFloating("link", ref, popover, "bottom-start", this.$refs.linkArrow);
    },
    closeLinkPopup() {
      this.showLinkPopup = false;
      this.linkUrl = "";
      this.linkText = "";
      this.teardownFloating("link");
      this.restoreFocus();
    },
    insertLink() {
      if (!this.linkUrl || this.linkUrl.trim() === "" || this.linkUrl === "https://") {
        this.closeLinkPopup();
        return;
      }

      const editor = this.$refs[this.editorRef];
      if (!editor) return;

      try {
        const quill = editor.getQuill();
        if (!quill) {
          this.closeLinkPopup();
          return;
        }

        const range = quill.getSelection(true);

        if (!range) {
          this.closeLinkPopup();
          return;
        }

        let url = this.linkUrl.trim();
        if (!url.startsWith("http://") && !url.startsWith("https://")) {
          url = "https://" + url;
        }

        const text = this.linkText.trim() || url;

        if (range.length > 0) {
          quill.formatText(range.index, range.length, "link", url);
        } else {
          quill.insertText(range.index, text, "link", url);
          quill.setSelection(range.index + text.length);
        }

        this.$nextTick(() => {
          this.addLinkAttributes();
        });

        this.closeLinkPopup();
      } catch (error) {
        this.closeLinkPopup();
      }
    },
    addLinkAttributes() {
      if (!this.isEditorReady) return;

      const editor = this.$refs[this.editorRef];
      if (!editor) return;

      try {
        const quill = editor.getQuill();
        if (!quill || !quill.root) return;
        if (!quill.emitter || typeof quill.emitter.emit !== "function") return;

        const links = quill.root.querySelectorAll(`a[href]`);
        links.forEach((link) => {
          if (!link.hasAttribute("target")) link.setAttribute("target", "_blank");
          if (!link.hasAttribute("rel")) link.setAttribute("rel", "noopener noreferrer");
          if (!link.style.color) link.style.color = "#2d60ff";
          if (!link.style.textDecoration) link.style.textDecoration = "underline";
        });
      } catch (error) {}
    },
    imageHandler() {
      try {
        if (this.enableImageUrl && !this.enableImageUpload) {
          this.imageInsertMode = "url";
        } else if (this.enableImageUpload && !this.enableImageUrl) {
          this.imageInsertMode = "upload";
        } else {
          this.imageInsertMode = "url";
        }

        this.imageUrl = "";
        this.imageAlt = "";
        this.rememberFocus();
        this.closeAllPopovers("image");
        this.showImagePopup = true;
        this.$nextTick(() => {
          this.positionImagePopover();
        });
      } catch (error) {
        // Error in imageHandler
      }
    },
    positionImagePopover() {
      const ref =
        this.getToolbarButton("button.ql-image") || this.$el?.querySelector(".ql-toolbar");
      const popover = this.$refs.imagePopover;
      this.attachFloating("image", ref, popover, "bottom-start", this.$refs.imageArrow);
    },
    triggerFileInput() {
      this.$refs.fileInput?.click();
      this.closeImagePopup();
    },
    closeImagePopup() {
      this.showImagePopup = false;
      this.imageUrl = "";
      this.imageAlt = "";
      this.resetUploadState();
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
      this.teardownFloating("image");
      this.restoreFocus();
    },
    insertImageFromUrl() {
      if (!this.imageUrl || this.imageUrl.trim() === "" || this.imageUrl.trim() === "https://") {
        return;
      }

      const editor = this.$refs[this.editorRef];
      if (!editor) return;

      try {
        const quill = editor.getQuill();
        if (!quill) {
          this.closeImagePopup();
          return;
        }

        const range = quill.getSelection(true);
        const insertIndex = range ? range.index : quill.getLength();

        let url = this.imageUrl.trim();
        if (!url.startsWith("http://") && !url.startsWith("https://") && !url.startsWith("data:")) {
          url = "https://" + url;
        }

        quill.insertEmbed(insertIndex, "image", url);

        this.$nextTick(() => {
          const insertedImg = quill.root.querySelector(`img[src="${url}"]`);
          if (insertedImg && this.imageAlt.trim()) {
            insertedImg.alt = this.imageAlt.trim();
          }
        });

        quill.setSelection(insertIndex + 1);
        this.closeImagePopup();
      } catch (error) {
        this.closeImagePopup();
      }
    },
    handleImageSelect(event) {
      try {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith("image/")) {
          this.$toast?.error?.("Please select an image file.");
          return;
        }

        if (this.showImagePopup) this.closeImagePopup();
        this.uploadImageFileFromSource(file);
      } catch (error) {
        this.$toast?.error?.("Error processing image.");
        this.resetUploadState();
      } finally {
        if (event && event.target) event.target.value = "";
      }
    },
    uploadImageFileFromSource(file, insertIndex) {
      if (!file) return;
      if (!file.type || !file.type.startsWith("image/")) {
        this.$toast?.error?.("Please select an image file.");
        return;
      }
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        this.$toast?.error?.("Image size must be less than 5MB.");
        return;
      }

      const editor = this.$refs[this.editorRef];
      if (!editor) return;

      const quill = editor.getQuill();
      if (!quill) return;

      let resolvedIndex = insertIndex;
      if (typeof resolvedIndex !== "number") {
        const range = quill.getSelection(true);
        resolvedIndex = range ? range.index : quill.getLength();
      }

      this.selectedFileName = file.name;
      this.isUploadingImage = true;

      const placeholderId = this.insertUploadPlaceholder(quill, resolvedIndex, "image", file);
      if (!placeholderId) return;

      const controller = new AbortController();
      this.uploadControllers.set(placeholderId, controller);
      this.bumpPendingUploads(1);

      const component = this;
      let cleanedUp = false;
      const cleanup = () => {
        if (cleanedUp) return;
        cleanedUp = true;
        if (component.uploadControllers.has(placeholderId)) {
          component.uploadControllers.delete(placeholderId);
          component.bumpPendingUploads(-1);
        }
      };

      const onProgress = (pct) => {
        if (controller.signal.aborted) return;
        if (pct >= 100) {
          setPlaceholderState(quill.root, placeholderId, "finalizing");
          updatePlaceholderProgress(quill.root, placeholderId, 100, "Processing…");
        } else {
          updatePlaceholderProgress(quill.root, placeholderId, pct);
        }
      };
      const onSuccess = async (imageUrl) => {
        try {
          if (controller.signal.aborted) return;
          await component.swapPlaceholder(quill, placeholderId, "image", imageUrl);
          component.resetUploadState();
          component.handleContentUpdate();
        } finally {
          cleanup();
        }
      };
      const onError = (msg) => {
        try {
          if (controller.signal.aborted) {
            component.removePlaceholder(quill, placeholderId);
          } else {
            component.errorPlaceholder(placeholderId, msg || "Upload failed");
          }
          component.resetUploadState();
        } finally {
          cleanup();
        }
      };

      this.$emit("upload", {
        file,
        kind: "image",
        placeholderId,
        signal: controller.signal,
        onProgress,
        onSuccess,
        onError,
      });
    },
    bumpPendingUploads(delta) {
      this.pendingUploadCount = Math.max(0, this.pendingUploadCount + delta);
      this.$emit("uploads-pending", this.pendingUploadCount);
    },
    resetUploadState() {
      this.selectedFileName = "";
      this.isUploadingImage = false;
    },
    makePlaceholderId() {
      return `up_${Math.random().toString(36).slice(2)}_${Date.now()}`;
    },
    insertUploadPlaceholder(quill, index, kind, file) {
      const id = this.makePlaceholderId();
      let previewUrl = "";
      if (kind === "image" && file && file.type && file.type.startsWith("image/")) {
        try {
          previewUrl = URL.createObjectURL(file);
          this.placeholderPreviewUrls.set(id, previewUrl);
        } catch (e) {
          previewUrl = "";
        }
      }
      try {
        quill.insertEmbed(
          index,
          "rte-upload-placeholder",
          {
            id,
            kind,
            fileName: file.name,
            fileSize: file.size,
            mimeType: file.type,
            previewUrl,
          },
          "silent"
        );
      } catch (e) {
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
          this.placeholderPreviewUrls.delete(id);
        }
        return null;
      }
      return id;
    },
    revokePlaceholderPreview(id) {
      const url = this.placeholderPreviewUrls?.get(id);
      if (url) {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {}
        this.placeholderPreviewUrls.delete(id);
      }
    },
    async swapPlaceholder(quill, id, finalBlotName, finalValue) {
      if (finalBlotName === "image" && typeof finalValue === "string" && finalValue) {
        try {
          if (quill && quill.root) {
            setPlaceholderState(quill.root, id, "finalizing");
            updatePlaceholderProgress(quill.root, id, 100, "Processing…");
          }
          const pre = new Image();
          pre.decoding = "async";
          pre.src = finalValue;
          if (typeof pre.decode === "function") {
            await pre.decode();
          } else {
            await new Promise((resolve) => {
              pre.onload = () => resolve(null);
              pre.onerror = () => resolve(null);
            });
          }
        } catch (e) {
          // decode failure — fall through and let <img> render its native broken state
        }
      }
      try {
        const pos = findPlaceholderIndex(quill, id);
        if (pos < 0) return;
        if (typeof quill.history?.cutoff === "function") quill.history.cutoff();
        quill.deleteText(pos, 1, "silent");
        quill.insertEmbed(pos, finalBlotName, finalValue, "user");
        if (typeof quill.history?.cutoff === "function") quill.history.cutoff();
        quill.setSelection(pos + 1, 0, "silent");
        if (finalBlotName === "image") {
          this.$nextTick(() => {
            try {
              const root = quill.root;
              if (!root) return;
              const imgs = root.querySelectorAll("img");
              const img = Array.from(imgs)
                .reverse()
                .find((el) => el.src === finalValue || el.getAttribute("src") === finalValue);
              const target = img || imgs[imgs.length - 1];
              if (target) {
                target.setAttribute("loading", "lazy");
                target.setAttribute("decoding", "async");
                if (id && !target.getAttribute("data-placeholder-id")) {
                  target.setAttribute("data-placeholder-id", id);
                }
              }
            } catch (e) {}
          });
        }
      } catch (e) {
      } finally {
        this.revokePlaceholderPreview(id);
      }
    },
    removePlaceholder(quill, id) {
      try {
        const pos = findPlaceholderIndex(quill, id);
        if (pos < 0) return;
        quill.deleteText(pos, 1, "silent");
      } catch (e) {
      } finally {
        this.revokePlaceholderPreview(id);
      }
    },
    updatePlaceholder(id, pct, status) {
      const editor = this.$refs[this.editorRef];
      if (!editor) return;
      try {
        const quill = editor.getQuill();
        if (!quill || !quill.root) return;
        updatePlaceholderProgress(quill.root, id, pct, status);
      } catch (e) {}
    },
    errorPlaceholder(id, msg) {
      const editor = this.$refs[this.editorRef];
      if (!editor) return;
      try {
        const quill = editor.getQuill();
        if (!quill || !quill.root) return;
        this.removePlaceholder(quill, id);
        if (msg) this.$toast?.error?.(msg);
      } catch (e) {}
    },
    stripAllPlaceholders() {
      const editor = this.$refs[this.editorRef];
      if (!editor) return;
      try {
        const quill = editor.getQuill();
        if (!quill || !quill.root) return;
        const nodes = quill.root.querySelectorAll(".rte-upload-placeholder");
        nodes.forEach((node) => {
          const id = node.getAttribute("data-upload-id") || "";
          const blot =
            (quill.scroll && typeof quill.scroll.find === "function"
              ? quill.scroll.find(node)
              : null) || (typeof Quill.find === "function" ? Quill.find(node) : null);
          if (blot) {
            const idx = quill.getIndex(blot);
            quill.deleteText(idx, 1, "silent");
          }
          if (id) this.revokePlaceholderPreview(id);
        });
      } catch (e) {}
    },
    cancelUpload(placeholderId) {
      if (!placeholderId) return;
      const controller = this.uploadControllers.get(placeholderId);
      if (controller) controller.abort();
      const editor = this.$refs[this.editorRef];
      if (editor) {
        try {
          const quill = editor.getQuill();
          if (quill) this.removePlaceholder(quill, placeholderId);
        } catch (e) {}
      }
      if (this.uploadControllers.has(placeholderId)) {
        this.uploadControllers.delete(placeholderId);
        this.bumpPendingUploads(-1);
      }
    },
    cancelAllUploads() {
      if (!this.uploadControllers || this.uploadControllers.size === 0) return;
      const ids = Array.from(this.uploadControllers.keys());
      ids.forEach((id) => this.cancelUpload(id));
    },
    hasPendingUploads() {
      return this.pendingUploadCount > 0;
    },
    fileAttachHandler() {
      try {
        this.rememberFocus();
        this.closeAllPopovers("file");
        this.showFilePopup = true;
        this.$nextTick(() => this.positionFilePopover());
      } catch (e) {}
    },
    positionFilePopover() {
      const ref =
        this.getToolbarButton("button.ql-attach-file") || this.$el?.querySelector(".ql-toolbar");
      const popover = this.$refs.filePopover;
      this.attachFloating("file", ref, popover, "bottom-start", this.$refs.fileArrow);
    },
    closeFilePopup() {
      this.showFilePopup = false;
      this.teardownFloating("file");
      this.restoreFocus();
    },
    triggerGenericFileInput() {
      this.$refs.genericFileInput?.click();
      this.closeFilePopup();
    },
    handleGenericFileSelect(event) {
      try {
        const file = event.target.files?.[0];
        if (!file) return;
        this.uploadGenericFileFromSource(file);
      } catch (e) {
        this.isUploadingFile = false;
      } finally {
        if (event && event.target) event.target.value = "";
      }
    },
    uploadGenericFileFromSource(file, insertIndex) {
      if (!file) return;
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        this.$toast?.error?.("Files must be 10 MB or smaller.");
        return;
      }
      const editor = this.$refs[this.editorRef];
      if (!editor) return;
      const quill = editor.getQuill();
      if (!quill) return;

      let resolvedIndex = insertIndex;
      if (typeof resolvedIndex !== "number") {
        const range = quill.getSelection(true);
        resolvedIndex = range ? range.index : quill.getLength();
      }

      this.isUploadingFile = true;
      this.fileAttachName = file.name;
      const placeholderId = this.insertUploadPlaceholder(quill, resolvedIndex, "file", file);
      if (!placeholderId) {
        this.isUploadingFile = false;
        return;
      }

      const controller = new AbortController();
      this.uploadControllers.set(placeholderId, controller);
      this.bumpPendingUploads(1);

      const component = this;
      const mimeType = file.type;
      const fileSize = file.size;
      const fileName = file.name;
      let cleanedUp = false;
      const cleanup = () => {
        if (cleanedUp) return;
        cleanedUp = true;
        if (component.uploadControllers.has(placeholderId)) {
          component.uploadControllers.delete(placeholderId);
          component.bumpPendingUploads(-1);
        }
      };

      this.$emit("upload", {
        file,
        kind: "file",
        placeholderId,
        signal: controller.signal,
        onProgress: (pct) => {
          if (controller.signal.aborted) return;
          if (pct >= 100) {
            setPlaceholderState(quill.root, placeholderId, "finalizing");
            updatePlaceholderProgress(quill.root, placeholderId, 100, "Processing…");
          } else {
            updatePlaceholderProgress(quill.root, placeholderId, pct);
          }
        },
        onSuccess(url) {
          try {
            if (controller.signal.aborted) return;
            const pos = findPlaceholderIndex(quill, placeholderId);
            if (pos < 0) return;
            const ext = (fileName || "").split(".").pop()?.toLowerCase() || "";
            const isAudio =
              (mimeType && mimeType.startsWith("audio/")) ||
              /^(mp3|wav|ogg|m4a|aac|flac)$/.test(ext);
            const isVideo =
              (mimeType && mimeType.startsWith("video/")) || /^(mp4|webm|mov|avi|mkv)$/.test(ext);
            if (typeof quill.history?.cutoff === "function") quill.history.cutoff();
            quill.deleteText(pos, 1, "silent");
            if (isVideo) {
              quill.insertEmbed(pos, "html5-video", { src: url, fileName }, "user");
              quill.setSelection(pos + 1, 0, "silent");
            } else {
              // audio block embed wasn't surviving Quill serialization — route audio through file-chip
              const audioExtMime = {
                mp3: "audio/mpeg",
                wav: "audio/wav",
                ogg: "audio/ogg",
                m4a: "audio/x-m4a",
                aac: "audio/aac",
                flac: "audio/flac",
              };
              const finalMime = isAudio ? mimeType || audioExtMime[ext] || "audio/mpeg" : mimeType;
              quill.insertEmbed(
                pos,
                "file-chip",
                { url, fileName, mimeType: finalMime, fileSize },
                "user"
              );
              quill.insertText(pos + 1, " ", "user");
              quill.setSelection(pos + 2, 0, "silent");
            }
            if (typeof quill.history?.cutoff === "function") quill.history.cutoff();
            component.handleContentUpdate();
          } catch (e) {
            // ignore
          } finally {
            component.isUploadingFile = false;
            component.fileAttachName = "";
            cleanup();
          }
        },
        onError(msg) {
          try {
            if (controller.signal.aborted) {
              component.removePlaceholder(quill, placeholderId);
            } else {
              component.errorPlaceholder(placeholderId, msg || "Upload failed");
            }
          } finally {
            component.isUploadingFile = false;
            component.fileAttachName = "";
            cleanup();
          }
        },
      });
    },
    uploadFileFromExternalSource(file, insertIndex) {
      if (!file) return;
      if (file.type && file.type.startsWith("image/")) {
        this.uploadImageFileFromSource(file, insertIndex);
      } else {
        this.uploadGenericFileFromSource(file, insertIndex);
      }
    },
    hasUploadListener() {
      if (this.acceptsUploads) return true;
      return typeof this.$?.vnode?.props?.onUpload === "function";
    },
    handleEditorPaste(e) {
      if (!this.hasUploadListener()) return;
      const items = e.clipboardData?.items;
      if (!items || items.length === 0) return;

      const files = [];
      for (const item of items) {
        if (item.kind === "file") {
          const f = item.getAsFile();
          if (f) files.push(f);
        }
      }
      if (files.length === 0) return;

      e.preventDefault();
      e.stopPropagation();

      const editor = this.$refs[this.editorRef];
      const quill = editor?.getQuill?.();
      if (!quill) return;
      const range = quill.getSelection(true);
      const baseIndex = range ? range.index : quill.getLength();
      files.forEach((f, i) => this.uploadFileFromExternalSource(f, baseIndex + i));
    },
    handleEditorDragOver(e) {
      const types = Array.from(e.dataTransfer?.types || []);
      if (!types.includes("Files")) return;
      e.preventDefault();
    },
    handleEditorDrop(e) {
      const files = Array.from(e.dataTransfer?.files || []);
      if (files.length === 0) return;
      e.preventDefault();
      if (!this.hasUploadListener()) return;
      e.stopPropagation();

      const editor = this.$refs[this.editorRef];
      const quill = editor?.getQuill?.();
      if (!quill) return;
      const range = quill.getSelection(true);
      const baseIndex = range ? range.index : quill.getLength();
      files.forEach((f, i) => this.uploadFileFromExternalSource(f, baseIndex + i));
    },
    insertImage(imageUrl, placeholderId) {
      const editor = this.$refs[this.editorRef];
      if (!editor) return;

      try {
        const quill = editor.getQuill();
        if (!quill) return;

        const range = quill.getSelection(true);
        const index = range ? range.index : quill.getLength();
        this.insertImageAtPosition(imageUrl, index, placeholderId);
      } catch (error) {
        // Error in insertImage
      }
    },
    insertImageAtPosition(imageUrl, index, placeholderId) {
      const editor = this.$refs[this.editorRef];
      if (!editor) return;

      try {
        const quill = editor.getQuill();
        if (!quill) return;

        const storedPlaceholderId = placeholderId;

        if (!imageUrl || (typeof imageUrl === "string" && imageUrl.trim() === "")) {
          return;
        }

        quill.insertEmbed(index, "image", imageUrl);

        if (storedPlaceholderId) {
          this.$nextTick(() => {
            const root = quill.root;
            if (!root) return;

            const images = root.querySelectorAll("img");
            const insertedImg = Array.from(images).find((img) => {
              const imgSrc = img.src || img.getAttribute("src") || "";
              const compareUrl = imageUrl || "";
              return (
                imgSrc === compareUrl || imgSrc.includes(compareUrl) || compareUrl.includes(imgSrc)
              );
            });

            const targetImg =
              insertedImg ||
              Array.from(images).find((img) => !img.getAttribute("data-placeholder-id")) ||
              (images.length > 0 ? images[images.length - 1] : null);

            if (targetImg) {
              targetImg.setAttribute("data-placeholder-id", storedPlaceholderId);

              if (!imageUrl.startsWith("data:")) {
                if (!targetImg.hasAttribute("loading")) {
                  targetImg.setAttribute("loading", "lazy");
                }
              }

              targetImg.style.display = "";
              targetImg.style.maxWidth = "100%";
              targetImg.style.height = "auto";

              if (targetImg.src !== imageUrl && !targetImg.getAttribute("src")) {
                targetImg.src = imageUrl;
              }

              targetImg.onerror = () => {
                // Image failed to load
              };
            }
          });
        }

        quill.setSelection(index + 1);

        setTimeout(() => {
          this.handleContentUpdate();
        }, 100);
      } catch (error) {
        // Error inserting image
      }
    },
    isHtmlEscaped(html) {
      if (!html || typeof html !== "string") return false;
      return /&lt;/.test(html) && !/</.test(html);
    },
    decodeIfEscaped(html) {
      if (!html || typeof html !== "string") return html || "";
      if (!this.isHtmlEscaped(html)) {
        return html;
      }
      const div = document.createElement("div");
      div.innerHTML = html;
      return div.innerHTML;
    },
    handleContentUpdate() {
      if (this.contentUpdateTimeout) {
        clearTimeout(this.contentUpdateTimeout);
      }

      this.contentUpdateTimeout = setTimeout(() => {
        if (!this.isEditorReady || !this.$refs[this.editorRef]) {
          return;
        }

        try {
          const editor = this.$refs[this.editorRef];
          const quill = editor.getQuill();
          if (!quill || !quill.emitter || typeof quill.emitter.emit !== "function") {
            return;
          }

          this.ensureLinkAttributes();
          const content = editor.getHTML();
          if (content !== this.modelValue) {
            this.$emit("update:modelValue", content);
          }
        } catch (error) {
          // Silently ignore content update errors
        }
      }, 150);
    },
    ensureLinkAttributes() {
      if (!this.isEditorReady) return;

      const editor = this.$refs[this.editorRef];
      if (!editor) return;

      try {
        const quill = editor.getQuill();
        if (!quill || !quill.root) return;
        if (!quill.emitter || typeof quill.emitter.emit !== "function") return;

        const root = quill.root;
        const links = root.querySelectorAll("a[href]");

        if (links.length === 0) return;

        links.forEach((link) => {
          const isChip = link.classList.contains("rte-file-chip") || link.hasAttribute("data-mime");
          if (isChip) {
            // strip legacy inline color/underline left by older versions
            if (link.style.color) link.style.color = "";
            if (link.style.textDecoration) link.style.textDecoration = "";
            return;
          }
          // Skip anchors that wrap a single image — no text styling needed
          const isImageLink = link.children.length === 1 && link.children[0].tagName === "IMG";
          if (isImageLink) return;

          if (!link.hasAttribute("target")) link.setAttribute("target", "_blank");
          if (!link.hasAttribute("rel")) link.setAttribute("rel", "noopener noreferrer");
          if (!link.style.color) {
            link.style.color = "#2d60ff";
          }
          if (!link.style.textDecoration) {
            link.style.textDecoration = "underline";
          }
        });
      } catch (error) {}
    },
    getHTML() {
      try {
        return this.$refs[this.editorRef]?.getHTML() || "";
      } catch (error) {
        return "";
      }
    },
    getText() {
      try {
        return this.$refs[this.editorRef]?.getText() || "";
      } catch (error) {
        return "";
      }
    },
    setHTML(html) {
      if (this.$refs[this.editorRef]) {
        try {
          const decodedHtml = this.decodeIfEscaped(html);
          this.$refs[this.editorRef].setHTML(decodedHtml);
          if (this.isEditorReady) {
            this.$nextTick(() => {
              this.ensureLinkAttributes();
            });
          }
        } catch (error) {
          // Silently ignore setHTML errors
        }
      }
    },
    setText(text) {
      if (this.$refs[this.editorRef]) {
        try {
          this.$refs[this.editorRef].setText(text);
        } catch (error) {}
      }
    },
    clear() {
      this.setHTML("");
    },
    insertTextAtCursor(text) {
      if (!text || !this.isEditorReady) return;
      const quill = this.quillInstance;
      if (!quill) return;
      try {
        const range = quill.getSelection(true);
        const index = range ? range.index : quill.getLength();
        quill.insertText(index, text, "user");
        quill.setSelection(index + text.length, 0, "silent");
        this.handleContentUpdate();
      } catch (error) {}
    },
    onEditorReady(quill) {
      if (!quill) return;

      try {
        this.quillInstance = quill;

        if (!quill.emitter || typeof quill.emitter.emit !== "function") {
          setTimeout(() => this.onEditorReady(quill), 50);
          return;
        }

        try {
          const attachBtn = this.$el?.querySelector(".ql-attach-file");
          if (attachBtn && !attachBtn.querySelector("svg") && !attachBtn.dataset.decorated) {
            attachBtn.setAttribute("title", "Attach file");
            attachBtn.setAttribute("aria-label", "Attach file");
            attachBtn.innerHTML =
              '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>';
            attachBtn.dataset.decorated = "true";
          }
        } catch (e) {}

        quill.keyboard.addBinding({
          key: "Enter",
          shiftKey: false,
          handler: () => {
            this.$emit("enter-pressed");
            return false;
          },
        });

        this.isEditorReady = true;

        if (this.modelValue) {
          this.$nextTick(() => {
            if (this.$refs[this.editorRef]) {
              this.setHTML(this.modelValue);
            }
          });
        }

        setTimeout(() => {
          this.setupImageClickHandler(quill);
        }, 100);
      } catch (error) {
        // Silently ignore editor ready errors
      }
    },
    setupImageClickHandler(quill) {
      if (!quill || !quill.root) return;

      if (this.imageClickHandler && quill.root) {
        try {
          quill.root.removeEventListener("click", this.imageClickHandler);
        } catch (e) {}
      }
      if (this.pasteHandler && quill.root) {
        try {
          quill.root.removeEventListener("paste", this.pasteHandler);
        } catch (e) {}
      }
      if (this.dropHandler && quill.root) {
        try {
          quill.root.removeEventListener("drop", this.dropHandler);
        } catch (e) {}
      }
      if (this.dragOverHandler && quill.root) {
        try {
          quill.root.removeEventListener("dragover", this.dragOverHandler);
        } catch (e) {}
      }

      const root = quill.root;

      const component = this;
      this.imageClickHandler = (e) => {
        try {
          const target = e.target;

          const cancelBtn = target && target.closest && target.closest('[data-action="cancel"]');
          if (cancelBtn) {
            const placeholder = cancelBtn.closest(".rte-upload-placeholder");
            if (placeholder) {
              e.preventDefault();
              e.stopPropagation();
              const id = placeholder.getAttribute("data-upload-id");
              if (id) component.cancelUpload(id);
              return;
            }
          }

          if (component.showImageResizePopup) {
            const popover = component.$refs.imageResizePopover;
            if (popover && !popover.contains(target) && target.tagName !== "IMG") {
              component.closeImageResizePopup();
              return;
            }
          }

          if (target && target.tagName === "IMG") {
            e.preventDefault();
            e.stopPropagation();
            setTimeout(() => {
              component.handleImageClick(target);
            }, 0);
          }
        } catch (error) {
          // Error in image click handler
        }
      };

      root.addEventListener("click", this.imageClickHandler);

      this.pasteHandler = (e) => component.handleEditorPaste(e);
      this.dropHandler = (e) => component.handleEditorDrop(e);
      this.dragOverHandler = (e) => component.handleEditorDragOver(e);
      root.addEventListener("paste", this.pasteHandler);
      root.addEventListener("drop", this.dropHandler);
      root.addEventListener("dragover", this.dragOverHandler);

      this.documentClickHandler = (e) => {
        const isInsideEditor = this.$el && this.$el.contains(e.target);
        const isInsideLink = this.$refs.linkPopover && this.$refs.linkPopover.contains(e.target);
        const isInsideImage = this.$refs.imagePopover && this.$refs.imagePopover.contains(e.target);
        const isInsideFile = this.$refs.filePopover && this.$refs.filePopover.contains(e.target);
        const isInsideResize =
          this.$refs.imageResizePopover && this.$refs.imageResizePopover.contains(e.target);
        const isInsideAny =
          isInsideEditor || isInsideLink || isInsideImage || isInsideFile || isInsideResize;

        if (this.showImageResizePopup && !isInsideAny) {
          this.closeImageResizePopup();
        }
        if (this.showLinkPopup && !isInsideAny) {
          this.closeLinkPopup();
        }
        if (this.showImagePopup && !isInsideAny) {
          this.closeImagePopup();
        }
        if (this.showFilePopup && !isInsideAny) {
          this.closeFilePopup();
        }
      };

      document.addEventListener("click", this.documentClickHandler);
    },
    handleImageClick(imgElement) {
      if (!imgElement) return;

      try {
        const editor = this.$refs[this.editorRef];
        if (editor) {
          const quill = editor.getQuill();
          if (quill && quill.root) {
            const allImages = quill.root.querySelectorAll("img");
            allImages.forEach((img) => {
              img.classList.remove("image-selected");
            });
          }
        }

        imgElement.classList.add("image-selected");

        this.selectedImageEl = imgElement;
        this.selectedImageSrc = imgElement.src;

        this.originalImageWidth = imgElement.naturalWidth || imgElement.width || 0;
        this.originalImageHeight = imgElement.naturalHeight || imgElement.height || 0;

        const widthAttr = imgElement.getAttribute("width");
        const heightAttr = imgElement.getAttribute("height");
        const widthStyle = imgElement.style.width || "";
        const heightStyle = imgElement.style.height || "";

        const currentWidth = widthAttr || widthStyle || "";
        const currentHeight = heightAttr || heightStyle || "";

        if (currentWidth) {
          if (typeof currentWidth === "number") {
            this.imageWidth = currentWidth;
          } else {
            const widthMatch = String(currentWidth).match(/(\d+)/);
            if (widthMatch) {
              this.imageWidth = parseInt(widthMatch[1], 10);
            } else {
              this.imageWidth = imgElement.offsetWidth || this.originalImageWidth;
            }
          }
        } else {
          this.imageWidth = imgElement.offsetWidth || this.originalImageWidth;
        }

        if (currentHeight) {
          if (typeof currentHeight === "number") {
            this.imageHeight = currentHeight;
          } else {
            const heightMatch = String(currentHeight).match(/(\d+)/);
            if (heightMatch) {
              this.imageHeight = parseInt(heightMatch[1], 10);
            } else {
              this.imageHeight = imgElement.offsetHeight || this.originalImageHeight;
            }
          }
        } else {
          this.imageHeight = imgElement.offsetHeight || this.originalImageHeight;
        }

        const parentAnchor = imgElement.closest("a");
        this.imageLinkUrl = parentAnchor ? parentAnchor.getAttribute("href") || "" : "";
        this.showImageLinkInput = false;

        this.closeAllPopovers("imageResize");
        this.showImageResizePopup = true;
        this.$nextTick(() => {
          this.positionImageResizePopover(imgElement);
          if (this.$refs.imageResizeInput) {
            this.$refs.imageResizeInput.focus();
            this.$refs.imageResizeInput.select();
          }
        });
      } catch (error) {
        // Error handling image click
      }
    },
    positionImageResizePopover(imgElement) {
      if (!imgElement || !this.$refs.imageResizePopover) return;
      this.attachFloating("imageResize", imgElement, this.$refs.imageResizePopover, "top");
    },
    closeImageResizePopup() {
      const editor = this.$refs[this.editorRef];
      if (editor) {
        const quill = editor.getQuill();
        if (quill && quill.root) {
          const allImages = quill.root.querySelectorAll("img");
          allImages.forEach((img) => {
            img.classList.remove("image-selected");
          });
        }
      }

      this.showImageResizePopup = false;
      this.selectedImageSrc = null;
      this.selectedImageEl = null;
      this.imageWidth = null;
      this.imageHeight = null;
      this.originalImageWidth = null;
      this.originalImageHeight = null;
      this.maintainAspectRatio = true;
      this.showImageLinkInput = false;
      this.imageLinkUrl = "";
      this.teardownFloating("imageResize");
    },
    toggleAspectRatio() {
      this.maintainAspectRatio = !this.maintainAspectRatio;
      if (this.maintainAspectRatio && this.originalImageWidth && this.originalImageHeight) {
        this.imageWidth = this.originalImageWidth;
        this.imageHeight = this.originalImageHeight;
      }
    },
    toggleImageLinkInput() {
      this.showImageLinkInput = !this.showImageLinkInput;
      if (this.showImageLinkInput) {
        this.$nextTick(() => {
          this.$refs.imageLinkInput?.focus();
        });
      }
    },
    sanitizeImageLinkUrl(raw) {
      const url = raw.trim();
      if (!url) return null;
      if (url.startsWith("/") || url.startsWith("#") || url.startsWith("?")) return url;
      try {
        const parsed = new URL(url);
        const allowed = ["http:", "https:", "mailto:", "tel:"];
        if (!allowed.includes(parsed.protocol)) return null;
        return parsed.href;
      } catch {
        // Bare hostname — assume https
        return "https://" + url;
      }
    },
    applyImageLink() {
      const safeUrl = this.sanitizeImageLinkUrl(this.imageLinkUrl);
      if (!safeUrl || !this.selectedImageEl) return;

      try {
        const imgElement = this.selectedImageEl;
        const parentAnchor = imgElement.closest("a");
        if (parentAnchor) {
          parentAnchor.setAttribute("href", safeUrl);
        } else {
          const anchor = document.createElement("a");
          anchor.setAttribute("href", safeUrl);
          anchor.setAttribute("target", "_blank");
          anchor.setAttribute("rel", "noopener noreferrer");
          imgElement.parentNode.insertBefore(anchor, imgElement);
          anchor.appendChild(imgElement);
        }
        this.imageLinkUrl = safeUrl;
        this.$nextTick(() => this.handleContentUpdate());
        this.showImageLinkInput = false;
      } catch (e) {
        console.error("Error applying image link:", e);
      }
    },
    removeImageLink() {
      if (!this.selectedImageEl) return;

      try {
        const imgElement = this.selectedImageEl;
        const parentAnchor = imgElement.closest("a");
        if (parentAnchor) {
          parentAnchor.parentNode.insertBefore(imgElement, parentAnchor);
          parentAnchor.remove();
          this.$nextTick(() => this.handleContentUpdate());
        }
        this.imageLinkUrl = "";
        this.showImageLinkInput = false;
      } catch (e) {
        console.error("Error removing image link:", e);
      }
    },
    applyImageResize() {
      if (!this.selectedImageSrc) {
        this.closeImageResizePopup();
        return;
      }

      const editor = this.$refs[this.editorRef];
      if (!editor) {
        this.closeImageResizePopup();
        return;
      }

      try {
        const quill = editor.getQuill();
        if (!quill || !quill.root) {
          this.closeImageResizePopup();
          return;
        }

        const root = quill.root;
        const imgElement = root.querySelector(`img[src="${this.selectedImageSrc}"]`);

        if (!imgElement) {
          this.closeImageResizePopup();
          return;
        }

        let finalWidth = this.imageWidth;
        let finalHeight = this.imageHeight;

        if (
          this.maintainAspectRatio &&
          finalWidth &&
          this.originalImageWidth &&
          this.originalImageHeight
        ) {
          const aspectRatio = this.originalImageHeight / this.originalImageWidth;
          finalHeight = Math.round(finalWidth * aspectRatio);
        }

        if (finalWidth) {
          imgElement.style.width = `${finalWidth}px`;
          imgElement.setAttribute("width", `${finalWidth}px`);
        } else {
          imgElement.style.width = "";
          imgElement.removeAttribute("width");
        }

        if (finalHeight) {
          imgElement.style.height = `${finalHeight}px`;
          imgElement.setAttribute("height", `${finalHeight}px`);
        } else {
          imgElement.style.height = "";
          imgElement.removeAttribute("height");
        }

        this.$nextTick(() => {
          this.handleContentUpdate();
        });

        this.closeImageResizePopup();
      } catch (error) {
        this.closeImageResizePopup();
      }
    },
  },
  created() {
    this.uploadControllers = new Map();
    this.placeholderPreviewUrls = new Map();
  },
  mounted() {
    this.$nextTick(() => {
      if (this.$refs[this.editorRef]) {
        const editor = this.$refs[this.editorRef];
        if (editor && typeof editor.getQuill === "function") {
          try {
            const quill = editor.getQuill();
            if (quill && quill.emitter && typeof quill.emitter.emit === "function") {
              this.quillInstance = quill;
              this.isEditorReady = true;

              if (this.modelValue) {
                this.$nextTick(() => {
                  this.setHTML(this.modelValue);
                });
              }
            }
          } catch (error) {
            // Silently ignore mount errors
          }
        }
      }
    });

    this.escKeyHandler = (e) => {
      if (e.key !== "Escape") return;
      if (this.showLinkPopup) {
        e.stopPropagation();
        this.closeLinkPopup();
      } else if (this.showImagePopup) {
        e.stopPropagation();
        this.closeImagePopup();
      } else if (this.showFilePopup) {
        e.stopPropagation();
        this.closeFilePopup();
      } else if (this.showImageResizePopup) {
        e.stopPropagation();
        this.closeImageResizePopup();
      }
    };
    document.addEventListener("keydown", this.escKeyHandler, true);
  },
  beforeUnmount() {
    this.cancelAllUploads();

    if (this.placeholderPreviewUrls && this.placeholderPreviewUrls.size > 0) {
      for (const url of this.placeholderPreviewUrls.values()) {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {}
      }
      this.placeholderPreviewUrls.clear();
    }

    if (this.contentUpdateTimeout) {
      clearTimeout(this.contentUpdateTimeout);
      this.contentUpdateTimeout = null;
    }

    this.teardownFloating("link");
    this.teardownFloating("image");
    this.teardownFloating("file");
    this.teardownFloating("imageResize");

    if (this.escKeyHandler) {
      document.removeEventListener("keydown", this.escKeyHandler, true);
      this.escKeyHandler = null;
    }

    if (this.quillInstance && this.quillInstance.root && this.imageClickHandler) {
      try {
        this.quillInstance.root.removeEventListener("click", this.imageClickHandler);
      } catch (e) {}
    }

    if (this.quillInstance && this.quillInstance.root) {
      try {
        if (this.pasteHandler) {
          this.quillInstance.root.removeEventListener("paste", this.pasteHandler);
        }
        if (this.dropHandler) {
          this.quillInstance.root.removeEventListener("drop", this.dropHandler);
        }
        if (this.dragOverHandler) {
          this.quillInstance.root.removeEventListener("dragover", this.dragOverHandler);
        }
      } catch (e) {}
    }

    if (this.documentClickHandler) {
      document.removeEventListener("click", this.documentClickHandler);
      this.documentClickHandler = null;
    }

    if (this.quillInstance) {
      try {
        if (typeof this.quillInstance.off === "function") {
          this.quillInstance.off("text-change");
          this.quillInstance.off("selection-change");
          this.quillInstance.off("editor-change");
        }

        if (this.quillInstance.scroll && this.quillInstance.scroll.domNode) {
          try {
            const scrollNode = this.quillInstance.scroll.domNode;
            if (scrollNode && scrollNode.parentNode) {
              const observer = this.quillInstance.scroll.observer;
              if (observer && typeof observer.disconnect === "function") {
                observer.disconnect();
              }
            }
          } catch (e) {}
        }

        if (this.quillInstance.emitter) {
          if (typeof this.quillInstance.emitter.off === "function") {
            this.quillInstance.emitter.off();
          }
          if (this.quillInstance.emitter && typeof this.quillInstance.emitter.emit === "function") {
            this.quillInstance.emitter.emit = function () {};
          }
        }
      } catch (e) {
        // Error during unmount cleanup
      }
    }

    this.quillInstance = null;
    this.isEditorReady = false;
    this.imageClickHandler = null;
    this.pasteHandler = null;
    this.dropHandler = null;
    this.dragOverHandler = null;

    if (this.$refs[this.editorRef]) {
      try {
        const editor = this.$refs[this.editorRef];
        if (editor && typeof editor.getQuill === "function") {
          const quill = editor.getQuill();
          if (quill && quill.root) {
            try {
              if (typeof quill.off === "function") {
                quill.off("text-change");
                quill.off("selection-change");
                quill.off("editor-change");
              }

              if (quill.scroll && quill.scroll.domNode) {
                const observer = quill.scroll.observer;
                if (observer && typeof observer.disconnect === "function") {
                  observer.disconnect();
                }
              }
            } catch (e) {
              // Error during observer cleanup
            }
          }
        }
      } catch (error) {
        // Error during editor cleanup
      }
    }
  },
  watch: {
    modelValue(newValue, oldValue) {
      if (newValue === oldValue) return;

      if (!this.isEditorReady) {
        const checkReady = setInterval(() => {
          if (this.isEditorReady && this.$refs[this.editorRef]) {
            clearInterval(checkReady);
            try {
              const quill = this.$refs[this.editorRef].getQuill();
              if (quill && quill.emitter && typeof quill.emitter.emit === "function") {
                this.setHTML(newValue || "");
              }
            } catch (error) {
              // Silently ignore content setting errors
            }
          }
        }, 50);

        setTimeout(() => clearInterval(checkReady), 2000);
        return;
      }

      if (this.$refs[this.editorRef]) {
        try {
          const quill = this.$refs[this.editorRef].getQuill();
          if (!quill || !quill.emitter || typeof quill.emitter.emit !== "function") {
            return;
          }
          const currentHtml = this.$refs[this.editorRef].getHTML();
          if (currentHtml !== newValue) {
            this.setHTML(newValue || "");
          }
        } catch (error) {
          // Silently ignore modelValue watch errors
        }
      }
    },
    imageWidth(newWidth) {
      if (
        this.maintainAspectRatio &&
        newWidth &&
        this.originalImageWidth &&
        this.originalImageHeight
      ) {
        const aspectRatio = this.originalImageHeight / this.originalImageWidth;
        this.imageHeight = Math.round(newWidth * aspectRatio);
      }
    },
  },
};
</script>

<style scoped>
.rich-text-editor {
  width: 100%;
  font-family: "Inter", sans-serif;
}

.rich-text-editor-wrapper {
  position: relative;
}

.rich-text-editor-wrapper:hover :deep(.ql-toolbar) {
  border-color: var(--color-border-hover);
}

.rich-text-editor-wrapper:hover :deep(.ql-container) {
  border-color: var(--color-border-hover);
}

:deep(.ql-toolbar) {
  padding: 8px 12px;
  font-size: 12px;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  background-color: #f9f9fc;
  border: 1px solid #e4e4e7;
  border-bottom: 1px solid #e4e4e7;
  transition: border-color 0.2s ease;
}

:deep(.ql-toolbar .ql-formats) {
  margin-right: 8px;
}

:deep(.ql-toolbar button) {
  padding: 4px 6px;
  font-size: 12px;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

:deep(.ql-toolbar button:hover) {
  background-color: #e8e8f0;
}

:deep(.ql-toolbar button.ql-active) {
  background-color: #e0e0f0;
}

:deep(.ql-toolbar .ql-picker) {
  font-size: 12px !important;
  color: #344054;
}

:deep(.ql-toolbar .ql-picker-options) {
  font-size: 12px;
}

:deep(.ql-toolbar .ql-picker-item) {
  font-size: 12px;
}

:deep(.ql-toolbar .ql-picker-label) {
  font-size: 12px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

:deep(.ql-toolbar .ql-picker-label:hover) {
  background-color: #e8e8f0;
}

:deep(.ql-toolbar .ql-stroke) {
  stroke: #344054;
}

:deep(.ql-toolbar .ql-fill) {
  fill: #344054;
}

:deep(.ql-container) {
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  font-size: 0.8125rem;
  font-family: "Inter", sans-serif;
  border: 1px solid #e4e4e7;
  border-top: 1px solid #e4e4e7;
  transition: border-color 0.2s ease;
}

:deep(.ql-editor) {
  font-family: "Inter", sans-serif;
  font-size: 0.8125rem;
  color: #2c2c2e;
  line-height: 1.6;
  padding: 12px 15px;
  min-height: 40px;
}

:deep(.ql-editor.ql-blank::before) {
  font-family: "Inter", sans-serif;
  font-style: normal;
  color: #9ca3af;
  font-size: 0.8125rem;
}

:deep(.ql-editor p) {
  margin: 0 0 16px 0;
}

/* .ql-container {
  height: 250px !important;
} */

:deep(.ql-editor p:last-child) {
  margin-bottom: 0;
}

:deep(.ql-editor h1),
:deep(.ql-editor h2),
:deep(.ql-editor h3),
:deep(.ql-editor h4),
:deep(.ql-editor h5),
:deep(.ql-editor h6) {
  font-family: "Inter", sans-serif;
  font-weight: 600;
  margin: 0 0 8px 0;
}

:deep(.ql-editor ul),
:deep(.ql-editor ol) {
  padding-left: 24px;
  margin: 0 0 8px 0;
}

:deep(.ql-editor li) {
  margin-bottom: 4px;
}

:deep(.ql-editor a) {
  color: #2d60ff;
  text-decoration: underline;
}

:deep(.ql-editor a:hover) {
  color: #1e4ed8;
}

.link-popover,
.image-popover,
.file-popover,
.image-resize-popover {
  font-family: "Inter", sans-serif;
}

.rte-popover {
  z-index: 10080;
  position: fixed;
  top: 0;
  left: 0;
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 10px;
  box-shadow:
    0 2px 4px rgba(15, 23, 42, 0.06),
    0 12px 28px rgba(15, 23, 42, 0.18);
  overflow: visible;
}

.rte-popover__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px 6px 12px;
  border-bottom: 1px solid #f1f5f9;
  background: #fafafa;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
}

.rte-popover__title {
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  letter-spacing: 0.2px;
}

.rte-popover__close {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  transition:
    background 0.15s ease,
    color 0.15s ease;
}
.rte-popover__close:hover {
  background: #e2e8f0;
  color: #0f172a;
}

.link-popover {
  width: 340px;
  min-width: 340px;
  max-width: min(340px, calc(100vw - 16px));
}

.link-popover__body {
  padding: 14px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.link-popover__actions {
  display: flex;
  gap: 8px;
  margin-top: 4px;
}

.image-popover,
.file-popover {
  width: 180px;
  min-width: 180px;
  max-width: min(180px, calc(100vw - 16px));
  display: flex;
  flex-direction: column;
  padding: 8px;
}

.rte-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
}

.rte-field__label {
  font-size: 12px;
  font-weight: 600;
  color: #0f172a;
}

.rte-field__input {
  width: 100%;
  box-sizing: border-box;
  padding: 8px 10px;
  font-size: 13px;
  line-height: 1.3;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  outline: none;
  transition: border-color 0.15s ease;
  background: #fff;
  color: #0f172a;
}

.rte-field__input:focus,
.rte-field__input--focus {
  border-color: #3e3efe;
  box-shadow: 0 0 0 3px rgba(62, 62, 254, 0.15);
}

.rte-check {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #334155;
  cursor: pointer;
  user-select: none;
}

.rte-check input[type="checkbox"] {
  width: 14px;
  height: 14px;
  accent-color: #3e3efe;
}

.rte-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.2;
  border-radius: 6px;
  cursor: pointer;
  transition:
    background 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;
}

.rte-btn--primary {
  background: #3e3efe;
  color: #ffffff;
  border: 1px solid #3e3efe;
}
.rte-btn--primary:hover:not(:disabled) {
  background: #2e2ed8;
  border-color: #2e2ed8;
}
.rte-btn--primary:disabled {
  background: #e5e7eb;
  border-color: #e5e7eb;
  color: #9ca3af;
  cursor: not-allowed;
}

.rte-btn--secondary {
  background: transparent;
  color: #0f172a;
  border: 1px solid #d1d5db;
}
.rte-btn--secondary:hover {
  background: #f1f5f9;
}

.rte-menu__item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  font-size: 13px;
  color: #0f172a;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s ease;
}
.rte-menu__item:hover:not(:disabled) {
  background: #f1f5f9;
}
.rte-menu__item:disabled {
  color: #9ca3af;
  cursor: not-allowed;
}
.rte-menu__item .iconify {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  color: #475569;
}

.rte-popover__arrow {
  position: absolute;
  width: 10px;
  height: 10px;
  background: #ffffff;
  transform: rotate(45deg);
  pointer-events: none;
}
.rte-popover__arrow[data-side="top"] {
  border-right: 1px solid rgba(15, 23, 42, 0.12);
  border-bottom: 1px solid rgba(15, 23, 42, 0.12);
}
.rte-popover__arrow[data-side="bottom"] {
  border-top: 1px solid rgba(15, 23, 42, 0.12);
  border-left: 1px solid rgba(15, 23, 42, 0.12);
}
.rte-popover__arrow[data-side="left"] {
  border-top: 1px solid rgba(15, 23, 42, 0.12);
  border-right: 1px solid rgba(15, 23, 42, 0.12);
}
.rte-popover__arrow[data-side="right"] {
  border-bottom: 1px solid rgba(15, 23, 42, 0.12);
  border-left: 1px solid rgba(15, 23, 42, 0.12);
}

.image-resize-popover {
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 8px;
  box-shadow:
    0 2px 4px rgba(15, 23, 42, 0.06),
    0 10px 24px rgba(15, 23, 42, 0.14);
  padding: 6px 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: auto;
  max-width: none;
  overflow: visible;
}

.image-resize-popover__controls {
  display: flex;
  align-items: center;
  gap: 6px;
}

.image-resize-popover__divider {
  width: 1px;
  height: 18px;
  background: #e5e7eb;
  margin: 0 2px;
}

.image-resize-popover__link-img-btn {
  padding: 4px 6px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: white;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.image-resize-popover__link-img-btn:hover {
  background: #f3f4f6;
  border-color: #9ca3af;
  color: #374151;
}

.image-resize-popover__link-img-btn--active {
  background: #3e3efe;
  color: white;
  border-color: #3e3efe;
}

.image-resize-popover__link-img-btn--active:hover {
  background: #2e2ed8;
  border-color: #2e2ed8;
}

.image-resize-popover__url-row {
  display: flex;
  align-items: center;
  gap: 4px;
  border-top: 1px solid #f1f5f9;
  padding-top: 6px;
}

.image-resize-popover__url-input {
  flex: 1;
  min-width: 160px;
  padding: 4px 6px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 12px;
  outline: none;
  transition: border-color 0.2s;
  background: white;
}

.image-resize-popover__url-input:focus {
  border-color: #3e3efe;
}

.image-resize-popover__url-apply {
  padding: 4px 6px;
  border: 1px solid #16a34a;
  border-radius: 4px;
  background: #16a34a;
  color: white;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.image-resize-popover__url-apply:hover:not(:disabled) {
  background: #15803d;
  border-color: #15803d;
}

.image-resize-popover__url-apply:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.image-resize-popover__url-remove {
  padding: 4px 6px;
  border: 1px solid #ef4444;
  border-radius: 4px;
  background: white;
  color: #ef4444;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.image-resize-popover__url-remove:hover {
  background: #fef2f2;
  border-color: #dc2626;
  color: #dc2626;
}

.image-resize-popover__input {
  width: 55px;
  padding: 4px 6px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 12px;
  outline: none;
  transition: border-color 0.2s;
  background: white;
  text-align: center;
}

.image-resize-popover__input:focus {
  border-color: #3e3efe;
  outline: none;
}

.image-resize-popover__px {
  font-size: 11px;
  color: #6b7280;
  white-space: nowrap;
  margin-right: 2px;
}

.image-resize-popover__link {
  padding: 4px 6px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: white;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.image-resize-popover__link:hover {
  background: #f3f4f6;
  border-color: #9ca3af;
  color: #374151;
}

.image-resize-popover__link--active {
  background: #3e3efe;
  color: white;
  border-color: #3e3efe;
}

.image-resize-popover__link--active:hover {
  background: #2e2ed8;
  border-color: #2e2ed8;
}

.image-resize-popover__apply {
  padding: 4px 6px;
  border: 1px solid #16a34a;
  border-radius: 4px;
  background: #16a34a;
  color: white;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-left: 2px;
}

.image-resize-popover__apply:hover {
  background: #15803d;
  border-color: #15803d;
}

:deep(.ql-editor img.image-selected) {
  outline: 2px solid #3e3efe;
  outline-offset: 2px;
  border-radius: 2px;
}

:deep(.ql-editor img) {
  max-width: 100%;
  height: auto;
  display: block;
  margin: 8px 0;
  object-fit: contain;
  opacity: 1;
  transition: opacity 0.1s ease-in;
}

:deep(.ql-editor img[src^="blob:"]) {
  max-width: 100%;
  height: auto;
  display: block;
  object-fit: contain;
  min-height: 1px;
  opacity: 1;
  transition: none;
}

:deep(.ql-editor img[src^="data:"]) {
  max-width: 100%;
  height: auto;
  display: block;
  object-fit: contain;
  opacity: 1;
  transition: none;
}

:deep(.ql-editor img:not([src])) {
  display: none;
}

:deep(.ql-editor img[data-placeholder-id]) {
  max-width: 100%;
  height: auto;
  display: block;
  object-fit: contain;
}

:deep(.ql-editor .rte-upload-placeholder) {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  margin: 4px 0;
  border: 1px solid #e0e7ff;
  background: #eef2ff;
  border-radius: 6px;
  user-select: none;
  position: relative;
  width: fit-content;
  max-width: 320px;
  min-width: 0;
}

:deep(.ql-editor .rte-upload-placeholder__icon) {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 4px;
  background: #ffffff;
}
:deep(.ql-editor .rte-upload-placeholder__icon .rte-file-icon) {
  width: 16px;
  height: 16px;
  background: transparent;
}

:deep(.ql-editor .rte-upload-placeholder--error) {
  border-color: #fca5a5;
  background: #fef2f2;
}

:deep(.ql-editor .rte-upload-placeholder--file .rte-upload-placeholder__body) {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
}

:deep(.ql-editor .rte-upload-placeholder--image .rte-upload-placeholder__body) {
  flex: 1;
  min-width: 0;
}

:deep(.ql-editor .rte-upload-placeholder--file .rte-upload-placeholder__name) {
  flex: 1;
  min-width: 0;
}

:deep(.ql-editor .rte-upload-placeholder--file .rte-upload-placeholder__track) {
  display: none;
}

:deep(.ql-editor .rte-upload-placeholder--image) {
  padding: 6px;
  gap: 10px;
  align-items: stretch;
}

:deep(.ql-editor .rte-upload-placeholder--image .rte-upload-placeholder__preview) {
  position: relative;
  flex-shrink: 0;
  width: 64px;
  height: 64px;
  border-radius: 6px;
  overflow: hidden;
  background: #e5e7eb;
}

:deep(.ql-editor .rte-upload-placeholder--image .rte-upload-placeholder__preview img) {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

:deep(.ql-editor .rte-upload-placeholder--image .rte-upload-placeholder__shade) {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.18);
  pointer-events: none;
}

:deep(.ql-editor .rte-upload-placeholder--image .rte-upload-placeholder__overlay) {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
}

:deep(.ql-editor .rte-upload-placeholder--finalizing .rte-upload-placeholder__shade),
:deep(.ql-editor .rte-upload-placeholder--error .rte-upload-placeholder__shade) {
  background: rgba(15, 23, 42, 0.08);
}

:deep(.ql-editor .rte-upload-placeholder__name) {
  font-size: 12px;
  color: #1e1b4b;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

:deep(.ql-editor .rte-upload-placeholder__meta) {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 10px;
  color: #6b7280;
  margin-top: 1px;
}

:deep(.ql-editor .rte-upload-placeholder__track) {
  height: 2px;
  background: #e5e7eb;
  border-radius: 2px;
  overflow: hidden;
  margin-top: 4px;
}

:deep(.ql-editor .rte-upload-placeholder__bar) {
  height: 100%;
  background: linear-gradient(90deg, #3e3efe, #6366f1);
  transition: width 0.15s ease;
}

@keyframes rte-upload-indeterminate {
  0% {
    background-position: 0 0;
  }
  100% {
    background-position: 24px 0;
  }
}

:deep(.ql-editor .rte-upload-placeholder--finalizing .rte-upload-placeholder__bar) {
  width: 100% !important;
  background-image:
    linear-gradient(
      -45deg,
      rgba(255, 255, 255, 0.35) 25%,
      transparent 25%,
      transparent 50%,
      rgba(255, 255, 255, 0.35) 50%,
      rgba(255, 255, 255, 0.35) 75%,
      transparent 75%,
      transparent
    ),
    linear-gradient(90deg, #3e3efe, #6366f1);
  background-size:
    24px 24px,
    100% 100%;
  animation: rte-upload-indeterminate 0.8s linear infinite;
  transition: none;
}

:deep(.ql-editor .rte-upload-placeholder__cancel) {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 1px solid transparent;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  padding: 0;
  margin-left: 2px;
  transition:
    background 0.15s ease,
    color 0.15s ease,
    border-color 0.15s ease;
}

:deep(.ql-editor .rte-upload-placeholder__cancel:hover) {
  background: #fee2e2;
  border-color: #fecaca;
  color: #ef4444;
}

:deep(.ql-editor .rte-upload-placeholder__cancel:focus-visible) {
  outline: 2px solid #6366f1;
  outline-offset: 1px;
}

:deep(.rte-file-icon) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  color: #64748b;
}

:deep(.rte-file-icon--pdf) {
  color: #dc2626;
}

:deep(.rte-file-icon--doc) {
  color: #2563eb;
}

:deep(.rte-file-icon--xls) {
  color: #16a34a;
}

:deep(.rte-file-icon--zip) {
  color: #d97706;
}

:deep(.rte-file-icon--img) {
  color: #0ea5e9;
}

:deep(.rte-file-icon--vid) {
  color: #7c3aed;
}

:deep(.rte-file-icon--aud) {
  color: #db2777;
}

:deep(.ql-editor .rte-file-chip),
.rte-file-chip {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 6px 10px 6px 6px;
  margin: 0 2px;
  border-radius: 8px;
  background: #eef2ff;
  color: #1e1b4b;
  text-decoration: none;
  border: 1px solid #e0e7ff;
  vertical-align: middle;
  line-height: 1.2;
  font-size: 12px;
  max-width: 100%;
}

:deep(.ql-editor .rte-file-chip:hover) {
  background: #e0e7ff;
}

:deep(.ql-editor .rte-file-chip .rte-file-icon) {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: #ffffff;
}
:deep(.ql-editor .rte-file-chip .rte-file-icon svg) {
  width: 18px;
  height: 18px;
  display: block;
}

:deep(.ql-editor .rte-file-chip__body),
.rte-file-chip__body {
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  gap: 2px;
}

:deep(.ql-editor .rte-file-chip__name),
.rte-file-chip__name {
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 220px;
  color: #1e1b4b;
}

:deep(.ql-editor .rte-file-chip__meta),
.rte-file-chip__meta {
  font-size: 10px;
  color: #6b7280;
  opacity: 1;
}

:deep(.ql-editor .rte-html5-video) {
  display: block;
  width: 100%;
  max-width: 480px;
  max-height: 360px;
  margin: 8px 0;
  border-radius: 10px;
  background: #0f172a;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
  outline: none;
}

:deep(.ql-editor .rte-html5-audio) {
  display: block;
  width: 100%;
  max-width: 380px;
  margin: 8px 0;
  border-radius: 999px;
  outline: none;
}
:deep(.ql-editor .rte-html5-audio::-webkit-media-controls-panel) {
  background-color: #eef2ff;
}

/* ── Font family picker ─────────────────────────────────────────────────── */

/* Apply fonts in the editor content */
:deep(.ql-editor .ql-font-arial) {
  font-family: Arial, sans-serif;
}
:deep(.ql-editor .ql-font-georgia) {
  font-family: Georgia, serif;
}
:deep(.ql-editor .ql-font-helvetica) {
  font-family: Helvetica, Arial, sans-serif;
}
:deep(.ql-editor .ql-font-verdana) {
  font-family: Verdana, Geneva, sans-serif;
}
:deep(.ql-editor .ql-font-trebuchet) {
  font-family: "Trebuchet MS", sans-serif;
}
:deep(.ql-editor .ql-font-times-new-roman) {
  font-family: "Times New Roman", Times, serif;
}
:deep(.ql-editor .ql-font-courier-new) {
  font-family: "Courier New", Courier, monospace;
}
:deep(.ql-editor .ql-font-impact) {
  font-family: Impact, Charcoal, sans-serif;
}

/* Toolbar picker button label */
:deep(.ql-snow .ql-picker.ql-font .ql-picker-label::before),
:deep(.ql-snow .ql-picker.ql-font .ql-picker-item::before) {
  content: "Default";
}
:deep(.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="arial"]::before),
:deep(.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="arial"]::before) {
  content: "Arial";
  font-family: Arial, sans-serif;
}
:deep(.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="georgia"]::before),
:deep(.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="georgia"]::before) {
  content: "Georgia";
  font-family: Georgia, serif;
}
:deep(.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="helvetica"]::before),
:deep(.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="helvetica"]::before) {
  content: "Helvetica";
  font-family: Helvetica, Arial, sans-serif;
}
:deep(.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="verdana"]::before),
:deep(.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="verdana"]::before) {
  content: "Verdana";
  font-family: Verdana, Geneva, sans-serif;
}
:deep(.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="trebuchet"]::before),
:deep(.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="trebuchet"]::before) {
  content: "Trebuchet MS";
  font-family: "Trebuchet MS", sans-serif;
}
:deep(.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="times-new-roman"]::before),
:deep(.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="times-new-roman"]::before) {
  content: "Times New Roman";
  font-family: "Times New Roman", Times, serif;
}
:deep(.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="courier-new"]::before),
:deep(.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="courier-new"]::before) {
  content: "Courier New";
  font-family: "Courier New", Courier, monospace;
}
:deep(.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="impact"]::before),
:deep(.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="impact"]::before) {
  content: "Impact";
  font-family: Impact, Charcoal, sans-serif;
}

/* Widen the font picker so long names aren't clipped */
:deep(.ql-snow .ql-picker.ql-font) {
  width: 105px;
}
</style>
